import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Search, Send, User, RefreshCw, AlertCircle, 
  CheckCircle, Clock, ShieldAlert, ArrowLeft, ExternalLink,
  ChevronRight, Sparkles, Filter, CornerDownLeft, Info
} from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerChats = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState('ALL'); // 'ALL' | 'DISPUTES' | 'ORDERS'
  const [sellerProfile, setSellerProfile] = useState(null);

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    fetchConversations();
  }, []);

  // Polling for new messages and active conversation updates
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(() => {
      fetchConversations(false);
      if (selectedUser?.user_id) {
        refreshActiveMessages(selectedUser.user_id);
      }
    }, 4000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchProfile = async () => {
    try {
      const res = await sellerAPI.getProfile();
      if (res.data?.success) {
        setSellerProfile(res.data.data);
      }
    } catch (_) {}
  };

  const fetchConversations = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await sellerAPI.getChats();
      if (res.data?.success) {
        const list = res.data.data || [];
        setConversations(list);

        // If no user is selected yet, or update selectedUser with latest metadata
        setSelectedUser(prev => {
          if (!prev && list.length > 0) return list[0];
          if (prev) {
            const updated = list.find(c => String(c.user_id) === String(prev.user_id));
            return updated || prev;
          }
          return null;
        });
      }
    } catch (err) {
      console.error("Error fetching seller chats:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const refreshActiveMessages = async (userId) => {
    try {
      const res = await sellerAPI.getChatMessages(userId);
      if (res.data?.success) {
        const msgs = res.data.messages || res.data.data || [];
        setChatMessages(msgs);
      }
    } catch (_) {}
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setChatMessages([]);
    setChatLoading(true);
    try {
      const res = await sellerAPI.getChatMessages(user.user_id);
      if (res.data?.success) {
        const msgs = res.data.messages || res.data.data || [];
        setChatMessages(msgs);
      }
    } catch (err) {
      console.error("Error loading chat messages:", err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUser || !chatInput.trim() || chatSending) return;
    const msgText = chatInput.trim();
    setChatSending(true);
    try {
      const res = await sellerAPI.sendChatMessage(selectedUser.user_id, { message: msgText });
      if (res.data?.success) {
        setChatInput('');
        const newMsg = res.data.data || {
          sender_id: sellerProfile?.user_id,
          text: msgText,
          created_at: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, newMsg]);
        fetchConversations(false);
      }
    } catch (err) {
      console.error("Failed to send chat message:", err);
      alert(err.response?.data?.message || "Failed to send message");
    } finally {
      setChatSending(false);
    }
  };

  const handleQuickReply = (text) => {
    setChatInput(text);
  };

  // Filtered conversations list
  const filteredConversations = conversations.filter(c => {
    const matchQuery = 
      (c.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.xoxo_id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.phone || '').includes(searchQuery);

    if (!matchQuery) return false;

    if (filterTab === 'DISPUTES') {
      return c.is_disputed || (c.withdrawal_status || '').toLowerCase() === 'disputed';
    }
    if (filterTab === 'ORDERS') {
      return !!c.withdrawal_id;
    }
    return true;
  });

  const disputesCount = conversations.filter(c => c.is_disputed || (c.withdrawal_status || '').toLowerCase() === 'disputed').length;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-2.5">
            <span className="p-2 rounded-2xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
              <MessageSquare size={22} />
            </span>
            <span>User Chats & Disputes</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            Direct real-time communication with users who placed withdrawal orders or reported payment disputes.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {disputesCount > 0 && (
            <div className="px-3 py-1.5 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-300 font-bold text-xs flex items-center gap-2 animate-pulse">
              <AlertCircle size={15} />
              <span>{disputesCount} User{disputesCount > 1 ? 's' : ''} Reported "Mere Pass Nahi Aaya"</span>
            </div>
          )}
          <button
            onClick={() => fetchConversations(true)}
            className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-slate-300 transition cursor-pointer"
            title="Refresh All Chats"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin text-emerald-400' : ''} />
          </button>
        </div>
      </div>

      {/* Main 2-Column Chat Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-[#0c1227]/90 rounded-3xl border border-white/10 shadow-2xl overflow-hidden h-[720px]">
        {/* Left Column: Conversations List (5 cols) */}
        <div className="lg:col-span-4 border-r border-white/10 flex flex-col h-full bg-[#0a0f22]/60">
          {/* Search & Tabs */}
          <div className="p-4 border-b border-white/10 space-y-3">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search user name or XOXO ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-900/80 border border-white/10 rounded-xl text-xs text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/30 font-medium"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-950/60 rounded-xl border border-white/5 text-[11px] font-bold">
              <button
                onClick={() => setFilterTab('ALL')}
                className={`flex-1 py-1.5 rounded-lg text-center transition cursor-pointer ${
                  filterTab === 'ALL' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                All ({conversations.length})
              </button>
              <button
                onClick={() => setFilterTab('DISPUTES')}
                className={`flex-1 py-1.5 rounded-lg text-center transition cursor-pointer flex items-center justify-center gap-1 ${
                  filterTab === 'DISPUTES' 
                    ? 'bg-rose-600 text-white shadow-sm' 
                    : disputesCount > 0 ? 'text-rose-400 hover:text-rose-300 font-black' : 'text-slate-400 hover:text-white'
                }`}
              >
                <span>Disputes</span>
                {disputesCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] flex items-center justify-center font-black">
                    {disputesCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setFilterTab('ORDERS')}
                className={`flex-1 py-1.5 rounded-lg text-center transition cursor-pointer ${
                  filterTab === 'ORDERS' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                Orders
              </button>
            </div>
          </div>

          {/* Conversations Scrollable List */}
          <div className="flex-1 overflow-y-auto divide-y divide-white/5">
            {loading ? (
              <div className="p-12 text-center text-slate-400 space-y-2">
                <div className="w-7 h-7 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-xs">Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-12 text-center text-slate-500 space-y-2">
                <MessageSquare size={36} className="mx-auto opacity-20" />
                <p className="text-xs font-semibold text-slate-400">No conversations found</p>
                <p className="text-[11px] text-slate-500 max-w-xs mx-auto">
                  When users request payouts or initiate inquiries, they will show up here.
                </p>
              </div>
            ) : (
              filteredConversations.map((item) => {
                const isSelected = selectedUser && String(selectedUser.user_id) === String(item.user_id);
                const hasDispute = item.is_disputed || (item.withdrawal_status || '').toLowerCase() === 'disputed';

                return (
                  <div
                    key={item.user_id}
                    onClick={() => handleSelectUser(item)}
                    className={`p-3.5 sm:p-4 cursor-pointer transition relative flex items-start gap-3 ${
                      isSelected 
                        ? 'bg-indigo-600/20 border-l-4 border-indigo-500' 
                        : hasDispute
                        ? 'bg-rose-950/20 hover:bg-rose-950/30'
                        : 'hover:bg-white/[0.03]'
                    }`}
                  >
                    {/* User Avatar */}
                    <div className="relative shrink-0">
                      <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-500 p-[1.5px] shadow-sm">
                        <div className="w-full h-full bg-[#0a0f22] rounded-[14px] flex items-center justify-center overflow-hidden font-black text-white text-sm">
                          {item.avatar_url ? (
                            <img src={item.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (item.user_name || 'U').charAt(0).toUpperCase()
                          )}
                        </div>
                      </div>
                      {hasDispute && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center text-[10px] font-black shadow-md animate-pulse">
                          !
                        </span>
                      )}
                    </div>

                    {/* Chat Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <h4 className="font-bold text-white text-xs sm:text-sm truncate">
                          {item.user_name}
                        </h4>
                        <span className="text-[10px] text-slate-500 whitespace-nowrap">
                          {item.last_message_time ? new Date(item.last_message_time).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-mono">
                          ID: {item.xoxo_id || item.user_id?.substring(0, 8)}
                        </span>
                        {item.unread_count > 0 && (
                          <span className="px-1.5 py-0.2 rounded-full bg-pink-500 text-white text-[9px] font-black">
                            {item.unread_count} new
                          </span>
                        )}
                      </div>

                      {/* Payment Dispute Warning Banner */}
                      {hasDispute && (
                        <div className="mt-1.5 px-2 py-1 rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-300 text-[10px] font-extrabold flex items-center gap-1.5">
                          <AlertCircle size={11} className="text-rose-400 shrink-0" />
                          <span className="truncate">Payment Not Received (Disputed)</span>
                        </div>
                      )}

                      {/* Withdrawal Request Badge (if non-disputed) */}
                      {!hasDispute && item.withdrawal_id && (
                        <div className="mt-1 text-[10px] text-amber-300 font-semibold flex items-center gap-1">
                          <span>₹{Number(item.amount_inr || 0).toLocaleString()}</span>
                          <span className="text-slate-500">•</span>
                          <span className="capitalize">{item.withdrawal_status?.replace(/_/g, ' ')}</span>
                        </div>
                      )}

                      {/* Last Message Snippet */}
                      <p className="text-xs text-slate-400 truncate mt-1">
                        {item.last_message ? item.last_message : (
                          <span className="italic text-slate-500">No message history yet</span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Chat Stream & Message Input (8 cols) */}
        <div className="lg:col-span-8 flex flex-col h-full bg-[#080d1e]/80">
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className="p-4 sm:p-5 border-b border-white/10 bg-[#0d142b]/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-500 p-[2px]">
                    <div className="w-full h-full bg-[#0a0f22] rounded-[14px] flex items-center justify-center overflow-hidden font-black text-white text-base">
                      {selectedUser.avatar_url ? (
                        <img src={selectedUser.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (selectedUser.user_name || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-extrabold text-white text-base flex items-center gap-2">
                      <span>{selectedUser.user_name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                        User
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">
                      XOXO ID: <strong className="text-indigo-300">{selectedUser.xoxo_id || selectedUser.user_id?.substring(0, 8)}</strong>
                      {selectedUser.phone && ` • Phone: ${selectedUser.phone}`}
                    </p>
                  </div>
                </div>

                {/* If User has a Withdrawal Order */}
                {selectedUser.withdrawal_id && (
                  <div className={`p-2 sm:p-2.5 rounded-xl border text-xs flex items-center gap-2.5 ${
                    selectedUser.is_disputed || (selectedUser.withdrawal_status || '').toLowerCase() === 'disputed'
                      ? 'bg-rose-500/15 border-rose-500/30 text-rose-200'
                      : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-200'
                  }`}>
                    <div>
                      <div className="flex items-center gap-2 text-[10px] uppercase font-black">
                        <span>Withdrawal #{selectedUser.withdrawal_id.substring(0, 8)}</span>
                        <span className={`px-1.5 py-0.2 rounded font-black ${
                          selectedUser.is_disputed ? 'bg-rose-500 text-white' : 'bg-white/10 text-white'
                        }`}>
                          {selectedUser.withdrawal_status}
                        </span>
                      </div>
                      <p className="font-bold text-white text-xs mt-0.5">
                        Amount: ₹{Number(selectedUser.amount_inr || 0).toLocaleString()} ({selectedUser.payout_type?.toUpperCase()})
                        {selectedUser.transaction_ref && ` • Ref: ${selectedUser.transaction_ref}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Disputed High-Priority Notice Banner */}
              {(selectedUser.is_disputed || (selectedUser.withdrawal_status || '').toLowerCase() === 'disputed') && (
                <div className="p-3 bg-rose-500/15 border-b border-rose-500/30 px-5 flex items-start gap-3 text-xs text-rose-200 animate-in fade-in">
                  <AlertCircle size={18} className="text-rose-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-rose-300 font-extrabold block">
                      ⚠️ User Reported Payment Not Received ("Mere Pass Nahi Aaya"):
                    </strong>
                    <span className="text-slate-300 text-[11px] block mt-0.5">
                      {selectedUser.dispute_reason || "User stated that the payment was not received."} 
                      {selectedUser.ticket_id && ` • Support Ticket #${String(selectedUser.ticket_id).substring(0, 8)} created for Admin Review.`}
                    </span>
                    <span className="text-[10px] text-amber-300 font-semibold mt-1 block">
                      💡 Tip: Share the bank UTR reference or payment screenshot here so the user can verify their bank account.
                    </span>
                  </div>
                </div>
              )}

              {/* Chat Messages Body */}
              <div className="flex-1 p-5 overflow-y-auto space-y-3.5 bg-gradient-to-b from-[#080d1e] to-[#0a0f24]">
                {chatLoading ? (
                  <div className="flex items-center justify-center h-full text-slate-400 text-xs gap-2">
                    <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Loading conversation...</span>
                  </div>
                ) : chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center p-6 space-y-2">
                    <MessageSquare size={42} className="opacity-20" />
                    <p className="text-sm font-bold text-slate-300">No messages in this chat yet</p>
                    <p className="text-xs text-slate-500 max-w-sm">
                      Send a message to user {selectedUser.user_name} to confirm payment details, share transaction references, or resolve queries.
                    </p>
                  </div>
                ) : (
                  chatMessages.map((msg, idx) => {
                    const isSeller = 
                      String(msg.sender_id) === String(sellerProfile?.user_id) || 
                      String(msg.sender_id) === String(sellerProfile?.id);

                    return (
                      <div
                        key={msg.id || idx}
                        className={`flex flex-col ${isSeller ? 'items-end' : 'items-start'}`}
                      >
                        <div className="flex items-end gap-2 max-w-[85%] sm:max-w-[70%]">
                          {!isSeller && (
                            <div className="w-7 h-7 rounded-xl bg-slate-800 text-slate-300 flex items-center justify-center text-xs font-bold shrink-0">
                              {(selectedUser.user_name || 'U').charAt(0)}
                            </div>
                          )}

                          <div
                            className={`p-3.5 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-sm ${
                              isSeller
                                ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-br-none'
                                : 'bg-slate-900 border border-white/10 text-slate-200 rounded-bl-none'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                            {msg.media_url && (
                              <a href={msg.media_url} target="_blank" rel="noreferrer" className="block mt-2">
                                <img src={msg.media_url} alt="" className="rounded-lg max-h-48 object-cover border border-white/10" />
                              </a>
                            )}
                            <div className={`flex items-center gap-1 justify-end mt-1 text-[9px] ${isSeller ? 'text-indigo-200' : 'text-slate-400'}`}>
                              <span>
                                {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                              {isSeller && (
                                <span title={msg.is_read ? 'Read' : 'Delivered'}>
                                  {msg.is_read ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Reply Suggestions */}
              <div className="px-5 py-2 bg-slate-950/40 border-t border-white/5 flex items-center gap-2 overflow-x-auto text-[11px]">
                <span className="text-slate-500 font-bold shrink-0">Quick Reply:</span>
                <button
                  type="button"
                  onClick={() => handleQuickReply(`I have completed your payment. UTR Ref: ${selectedUser.transaction_ref || '34635365'}. Please check your account.`)}
                  className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition shrink-0 whitespace-nowrap cursor-pointer"
                >
                  "Payment completed. Please check UTR."
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickReply("Please check your bank/UPI statement. The money has already been deducted from my account.")}
                  className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition shrink-0 whitespace-nowrap cursor-pointer"
                >
                  "Check bank statement"
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickReply("Please send your QR code or updated UPI ID so I can re-verify.")}
                  className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition shrink-0 whitespace-nowrap cursor-pointer"
                >
                  "Send updated UPI/QR"
                </button>
              </div>

              {/* Message Input Box */}
              <form onSubmit={handleSendMessage} className="p-4 bg-slate-950/80 border-t border-white/10 flex items-center gap-3">
                <input
                  type="text"
                  placeholder={`Type a message to ${selectedUser.user_name}...`}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={chatSending}
                  className="flex-1 px-4 py-3 bg-slate-900 border border-white/15 rounded-2xl text-xs sm:text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/40"
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || chatSending}
                  className="px-5 py-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-black text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  {chatSending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Send</span>
                      <Send size={15} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center p-8 space-y-3">
              <div className="w-16 h-16 rounded-3xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                <MessageSquare size={32} />
              </div>
              <h3 className="font-extrabold text-white text-lg">Select a Conversation</h3>
              <p className="text-xs text-slate-400 max-w-sm">
                Choose a user on the left to start chatting, review payout disputes, or respond to "Mere Pass Nahi Aaya" payment reports.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerChats;
